import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Brand } from '../../shared/models';
import { AuthService } from '../auth/auth.service';
import { BrandService } from './brand.service';
import { UserService } from './user.service';

const STORAGE_KEY = 'ln.currentBrandId';

/**
 * App-wide "current brand" context. Populated once per session from the signed-in user's
 * UserRoles; every brand-scoped screen should read currentBrand()/currentBrandId() instead of
 * keeping its own brand selection, so switching brands here re-filters everything reactively.
 */
@Injectable({ providedIn: 'root' })
export class BrandContextService {
  private authService = inject(AuthService);
  private brandService = inject(BrandService);
  private userService = inject(UserService);

  private readonly _availableBrands = signal<Brand[]>([]);
  private readonly _currentBrandId = signal<string | null>(null);
  private readonly _loading = signal(false);
  private readonly _initialized = signal(false);

  readonly availableBrands = this._availableBrands.asReadonly();
  readonly currentBrandId = this._currentBrandId.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly initialized = this._initialized.asReadonly();
  readonly currentBrand = computed(() =>
    this._availableBrands().find(b => b.id === this._currentBrandId()) ?? null
  );

  private bootstrapped = false;

  /** Call once after sign-in (from ShellComponent, which only mounts past authGuard). */
  async bootstrap(): Promise<void> {
    if (this.bootstrapped) return;
    this.bootstrapped = true;
    this._loading.set(true);

    try {
      const email = this.authService.getCurrentUser()?.email ?? null;
      const appUser = email ? await firstValueFrom(this.userService.searchByEmail(email)).catch(() => null) : null;

      const brandIds = appUser?.userRoles ? [...new Set(appUser.userRoles.map(r => r.brandId))] : [];

      // Global admins, users with no app-user record yet, and (pre-enforcement fallback) any
      // user with no role data at all see every brand - matching today's unscoped behavior.
      // Remove the "no role data" branch of this once UserRole data + server-side enforcement
      // are universal, so a genuinely brand-less user sees zero brands instead of all of them.
      const wantsAllBrands = !appUser || appUser.isGlobalAdmin || brandIds.length === 0;

      const brands = wantsAllBrands
        ? await this.fetchAllBrands()
        : await this.fetchBrandsByIds(brandIds);

      this._availableBrands.set(brands);
      this.resolveInitialSelection(brands);
    } finally {
      this._loading.set(false);
      this._initialized.set(true);
    }
  }

  setCurrentBrand(brandId: string): void {
    if (!this._availableBrands().some(b => b.id === brandId)) return;
    this._currentBrandId.set(brandId);
    localStorage.setItem(STORAGE_KEY, brandId);
  }

  private async fetchAllBrands(): Promise<Brand[]> {
    const response = await firstValueFrom(this.brandService.search('', 0, 200)).catch(() => null);
    return response?.items ?? [];
  }

  private async fetchBrandsByIds(brandIds: string[]): Promise<Brand[]> {
    const fetched = await Promise.all(
      brandIds.map(id => firstValueFrom(this.brandService.get(id)).catch(() => null))
    );
    return fetched.filter((b): b is Brand => !!b);
  }

  private resolveInitialSelection(brands: Brand[]): void {
    const persisted = localStorage.getItem(STORAGE_KEY);
    const fromQuery = new URLSearchParams(window.location.search).get('brandId');
    const candidate = [persisted, fromQuery].find(id => !!id && brands.some(b => b.id === id));
    this._currentBrandId.set(candidate ?? brands[0]?.id ?? null);
  }
}
